import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Building2, CheckCircle2, Clock, IdCard, Mail, Smartphone, XCircle } from 'lucide-react';
import { BACKEND_URL } from '@/config';
import { STEP_HINTS, VERIFICATION_TYPES, nextStep, stepLink } from '@/utils/verifications';

const KIND_ICONS = { email: Mail, phone: Smartphone, identity: IdCard, company: Building2 };
const STATE_ICONS = { VERIFIED: CheckCircle2, PENDING: Clock, REJECTED: XCircle };

/**
 * Date d'envoi et motif de refus de la pièce d'identité (`GET /users/me/kyc`), demandés seulement quand l'étape est
 * en cours d'examen ou refusée.
 */
export function useIdentityDetail(steps) {
  const identity = steps.find((s) => s.kind === 'identity');
  const wanted = identity?.state === 'PENDING' || identity?.state === 'REJECTED';
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (!wanted) return undefined;
    let alive = true;
    axios.get(`${BACKEND_URL}/api/users/me/kyc`)
      .then(({ data }) => { if (alive) setDetail(data?.submission || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [wanted]);
  return detail;
}

function hintOf(step, identity) {
  if (step.state === 'VERIFIED') return step.kind === 'email' || step.kind === 'phone' ? 'Vérifié' : 'Vérifiée';
  if (step.state === 'PENDING') {
    if (step.kind === 'company') return 'En cours de vérification par notre équipe';
    const sent = identity?.submitted_at ? ` · envoyée le ${new Date(identity.submitted_at).toLocaleDateString('fr-FR')}` : '';
    return `En cours d’examen${sent} · réponse sous 24 h`;
  }
  if (step.state === 'REJECTED') return `Refusée${identity?.reject_reason ? ` — ${identity.reject_reason}` : ''}`;
  return STEP_HINTS[step.kind] || '';
}

/**
 * Liste des étapes d'une action (`stepsFor`) : faite, à faire (bouton), en cours d'examen (sans bouton), refusée
 * (motif, « Renvoyer »). Le bouton de la prochaine étape est mis en avant ; chaque lien ramène à `from`.
 * `actions={false}` : liste seule (la fenêtre a son propre bouton).
 */
export default function VerificationSteps({ steps, from, identity, actions = true }) {
  const next = nextStep(steps);
  return (
    <ul className="vg-steps">
      {steps.map((step) => {
        const meta = VERIFICATION_TYPES[step.kind] || { label: step.kind, cta: 'Vérifier' };
        const Icon = STATE_ICONS[step.state] || KIND_ICONS[step.kind] || CheckCircle2;
        const actionable = step.state === 'NONE' || step.state === 'REJECTED';
        const testid = step.state === 'VERIFIED' ? `verification-done-${step.kind}` : `verification-missing-${step.kind}`;
        return (
          <li key={step.kind} className={`vg-step vg-step--${step.state}`} data-testid={testid}>
            <span className="vg-step-icon"><Icon size={20} aria-hidden="true" /></span>
            <div className="vg-step-body">
              <div className="vg-step-label">{meta.label}</div>
              <div className="vg-step-hint">{hintOf(step, identity)}</div>
            </div>
            {actions && actionable && (
              <Link to={stepLink(step.kind, from)} data-testid={`verification-cta-${step.kind}`}
                className={`vg-btn ${next?.kind === step.kind ? 'vg-btn--primary' : 'vg-btn--ghost'}`}>
                {step.state === 'REJECTED' ? 'Renvoyer ma pièce' : meta.cta}
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}
